import {
  EObrigacoesStatus,
  IObrigacaoCardProps,
  IObrigacoesNotification,
  IObrigacoesCardItemProps,
  IObrigacoesProps
} from "@/features/contabil/dashboard/widgets/Obrigacoes/types";
import {
  EObrigacoesFiscaisEmpresasObrigacoesStatus,
  IObrigacoesFiscaisEmpresas,
  IObrigacoesFiscaisEmpresasObrigacoes,
  IObrigacoesFiscaisResponse,
  ObrigacoesData,
  ObrigacoesPorMes
} from "../requests/types";
import { injectable, inject } from "inversify";
import "reflect-metadata";
import { DITypes } from "@/core/@types/types";
import type { IF360Date } from "@/core/plugins/date/F360Date";
import { RegimeTributarioEnum } from "@/features/contabil/@types/types";
import EmpresaMapper from "@/features/contabil/common/EmpresaMapperAdapter";

const MESES = 12;

@injectable()
export default class ObrigacoesFiscais {
  response: IObrigacoesFiscaisResponse | undefined;

  @inject(DITypes.DATE)
  private dateLib!: IF360Date;

  getNotifications(): IObrigacoesNotification[] {
    const notificacoes = Object.entries(this.response?.notificacoes || []);

    return notificacoes.map(([key, value]) => ({
      value,
      year: key
    }));
  }

  handleObrigacoesStatus(
    status: EObrigacoesFiscaisEmpresasObrigacoesStatus
  ): EObrigacoesStatus {
    const statusMap: Record<
      EObrigacoesFiscaisEmpresasObrigacoesStatus,
      EObrigacoesStatus
    > = {
      [EObrigacoesFiscaisEmpresasObrigacoesStatus.None]:
        EObrigacoesStatus.white,
      [EObrigacoesFiscaisEmpresasObrigacoesStatus.NaoEntregue]:
        EObrigacoesStatus.red,
      [EObrigacoesFiscaisEmpresasObrigacoesStatus.Entregue]:
        EObrigacoesStatus.green,
      [EObrigacoesFiscaisEmpresasObrigacoesStatus.Desconhecido]:
        EObrigacoesStatus.without
    };

    return statusMap[status];
  }

  private handleObrigacoesData(
    data: ObrigacoesData,
    year?: number
  ): IObrigacoesCardItemProps[] {
    if (!data) return [];

    const obrigacoesOrdenadasPorPeriodo = [...Object.entries(data)].sort(
      ([key], [keyPrev]) => Number(key) - Number(keyPrev)
    );

    return obrigacoesOrdenadasPorPeriodo.map(([key, value]) => ({
      title: key,
      label: key,
      status: this.handleObrigacoesStatus(value),
      statusValue: value,
      year
    }));
  }

  private handleObrigacoesMensais(
    data: ObrigacoesPorMes
  ): IObrigacoesCardItemProps[] {
    const ano = Object.entries(data).map(([year, data]) => ({ [year]: data }));

    const meses = ano.map((value) => {
      const [year, months] = Object.entries(value)[0];

      return this.handleObrigacoesData(months, Number(year));
    });

    return meses.flat();
  }

  private regimesNaoPermitidoParaEmpresasSimples(): string[] {
    return ["efd", "dctf"];
  }

  private regimesNaoPermitidoParaEmpresasNaoSimples(): string[] {
    return ["pgdas", "das"];
  }

  private handleObrigacoesRegimeSimples(
    regimes: [string, IObrigacoesFiscaisEmpresasObrigacoes | ObrigacoesPorMes][]
  ) {
    return regimes.filter(
      ([regime]) =>
        !this.regimesNaoPermitidoParaEmpresasSimples().includes(
          regime.toLowerCase()
        )
    );
  }

  private handleObrigacoesRegimeNaoSimples(
    regimes: [string, IObrigacoesFiscaisEmpresasObrigacoes | ObrigacoesPorMes][]
  ): [string, IObrigacoesFiscaisEmpresasObrigacoes | ObrigacoesPorMes][] {
    return regimes.filter(
      ([regime]) =>
        !this.regimesNaoPermitidoParaEmpresasNaoSimples().includes(
          regime.toLowerCase()
        )
    );
  }

  private handleObrigacoesPorRegime(
    obrigacoes: [
      string,
      IObrigacoesFiscaisEmpresasObrigacoes | ObrigacoesPorMes
    ][],
    regime?: RegimeTributarioEnum
  ): [string, IObrigacoesFiscaisEmpresasObrigacoes | ObrigacoesPorMes][] {
    if (regime === RegimeTributarioEnum.Simples)
      return this.handleObrigacoesRegimeSimples(obrigacoes);

    return this.handleObrigacoesRegimeNaoSimples(obrigacoes);
  }

  private handleObrigacoes(
    obrigacao: Record<string, ObrigacoesPorMes | ObrigacoesData> | null
  ): IObrigacaoCardProps[] {
    if (obrigacao === null) return [];

    const obrigacoes = Object.entries(obrigacao);

    const obrigacoesAnuaisEMensais = obrigacoes.map(([key, value]) => {
      const isMensal = Object.values(value).some(
        (data) => typeof data !== "number"
      );

      const obrigacoes = isMensal
        ? this.handleObrigacoesMensais(value as ObrigacoesPorMes)
        : this.handleObrigacoesData(value as ObrigacoesData);

      const data = this.isObrigacaoEmpty(obrigacoes) ? [] : obrigacoes;

      return {
        data,
        obrigacao: key,
        isMensal
      };
    });

    return obrigacoesAnuaisEMensais;
  }

  formatToCard(empresa: IObrigacoesFiscaisEmpresas): IObrigacoesProps {
    return {
      empresa: EmpresaMapper.toEntity(empresa),
      obrigacoes: this.handleObrigacoes(empresa.obrigacoes)
    };
  }

  handleMensalDataNaoPreenchidos = (month: number, year: number) => {
    // acrescento mais 1, porque a lib retorna de 0 a 11
    const mesAtual = month + 1;

    return {
      title: mesAtual.toString(),
      label: mesAtual.toString(),
      year: year,
      status: EObrigacoesStatus.without,
      statusValue: EObrigacoesFiscaisEmpresasObrigacoesStatus.None
    };
  };

  private handleBuildMonths = (
    meses: number[],
    obrigacoesPorMes: IObrigacoesCardItemProps[],
    anoAtual: number
  ): (IObrigacoesCardItemProps | undefined)[] => {
    return (
      meses.map((item) => {
        const mesDaObrigacaoJaPossuiValor = obrigacoesPorMes.find(
          (mes) => Number(mes.label) - 1 === Number(item)
        );

        if (mesDaObrigacaoJaPossuiValor) return mesDaObrigacaoJaPossuiValor;

        return this.handleMensalDataNaoPreenchidos(item, anoAtual);
      }) || []
    );
  };

  private obrigacoesByCurrentYear(
    data: IObrigacoesCardItemProps[],
    year: number
  ) {
    return data.filter((obrigacao) => Number(obrigacao.year) === year);
  }

  isObrigacaoEmpty(data: IObrigacoesCardItemProps[]) {
    const mesesNaoPreenchidos = MESES - data.length;

    const nenhumMesPreenchido = mesesNaoPreenchidos === 12;

    return nenhumMesPreenchido;
  }

  isObrigacaoPreenchida(data: IObrigacoesCardItemProps[]) {
    const mesesNaoPreenchidos = MESES - data.length;

    const todosMesesPreenchidos = mesesNaoPreenchidos === 0;

    return todosMesesPreenchidos;
  }

  handleFillMensal(
    data: IObrigacoesCardItemProps[],
    anoAtual: number
  ): IObrigacoesCardItemProps[] {
    const obrigacoesPorMes = this.obrigacoesByCurrentYear(data, anoAtual);

    if (this.isObrigacaoPreenchida(obrigacoesPorMes)) return obrigacoesPorMes;

    if (this.isObrigacaoEmpty(obrigacoesPorMes)) return [];

    const listaDosMeses = this.handleBuildMonths(
      [...Array.from({ length: MESES }).keys()],
      obrigacoesPorMes,
      anoAtual
    );

    const mesesSemValoresNaoPreenchidos = listaDosMeses.filter(
      (mes) => mes != undefined
    ) as IObrigacoesCardItemProps[];

    return mesesSemValoresNaoPreenchidos;
  }
}
